import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  ArrowPathIcon,
  CheckCircleIcon,
  DocumentArrowUpIcon,
  EnvelopeIcon,
  ExclamationTriangleIcon,
  FolderOpenIcon,
  PaperAirplaneIcon,
  TrashIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { debtorFullName, filingDebtors } from '../utils/debtors';

const API_URL = process.env.REACT_APP_API_URL || '';

const inputClass =
  'mt-1 block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500';

function Section({ number, title, description, children }) {
  return (
    <section className="rounded-xl border border-gray-200 bg-white shadow-sm">
      <div className="border-b border-gray-100 px-5 py-4 sm:px-6">
        <div className="flex items-start gap-3">
          <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-indigo-600 text-sm font-semibold text-white">
            {number}
          </span>
          <div>
            <h2 className="text-base font-semibold text-gray-900">{title}</h2>
            {description && <p className="mt-1 text-sm text-gray-500">{description}</p>}
          </div>
        </div>
      </div>
      <div className="px-5 py-5 sm:px-6">{children}</div>
    </section>
  );
}

function money(value) {
  const parsed = Number(value || 0);
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(parsed);
}

function dateLabel(value) {
  if (!value) return 'Date not entered';
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

function docketEntryLabel(item, maxLength = 180) {
  const label = String(
    item.docket_text || item.description || `Docket document ${item.id}`,
  ).replace(/\s+/g, ' ').trim();
  return label.length > maxLength ? `${label.slice(0, maxLength - 1).trimEnd()}…` : label;
}

function adversaryDeadline(meetingDateValue = null) {
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  let startingDate = today;
  let meetingDate = null;
  if (meetingDateValue) {
    const calendarDate = String(meetingDateValue).match(/^(\d{4})-(\d{2})-(\d{2})$/);
    meetingDate = calendarDate
      ? new Date(
        Number(calendarDate[1]),
        Number(calendarDate[2]) - 1,
        Number(calendarDate[3]),
        12,
      )
      : new Date(meetingDateValue);
  }
  if (meetingDate && !Number.isNaN(meetingDate.getTime())) {
    meetingDate.setHours(12, 0, 0, 0);
    if (meetingDate > startingDate) startingDate = meetingDate;
  }
  const deadline = new Date(startingDate);
  deadline.setDate(deadline.getDate() + 15);
  return deadline;
}

function defaultDemandDate(caseData = null) {
  const deadline = adversaryDeadline(caseData?.meeting_date);
  return deadline.toISOString().slice(0, 10);
}

function longDate(value) {
  const parsed = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value || '');
  return parsed.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

function emailAttachmentOptions(generation) {
  const explicit = Array.isArray(generation?.email_attachments)
    ? generation.email_attachments
    : [];
  if (explicit.length) return explicit;
  return (generation?.onedrive_files || [])
    .filter((item) => item.kind === 'service_packet' && item.filename)
    .map((item) => ({
      filename: item.filename,
      recipient_name: item.recipient_name || '',
    }));
}

function EmailPreview({ draft }) {
  const garnishments = draft.garnishments || [];
  const totalGarnished = garnishments.reduce(
    (sum, item) => sum + (Number(item.amount_value) || 0),
    0,
  );
  const returned = Number(draft.recoveredAmount) || 0;
  const remaining = Math.max(totalGarnished - returned, 0);

  return (
    <div>
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Email preview</p>
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-gray-100 p-3 sm:p-5">
        <div className="mx-auto max-w-2xl overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="border-b-4 border-indigo-500 bg-gray-800 px-5 py-5 text-white sm:px-7">
            <p className="text-xs font-bold uppercase tracking-widest text-indigo-200">Cisowski Law</p>
            <h3 className="mt-1 text-xl font-semibold">Demand for Return of Garnished Funds</h3>
            <p className="mt-1 text-sm text-gray-300">Automatic stay and garnishment documentation enclosed</p>
          </div>
          <div className="space-y-5 px-5 py-6 text-sm text-gray-700 sm:px-7">
            <p className="whitespace-pre-line leading-6">{draft.body}</p>

            <div className="overflow-hidden rounded-lg border border-gray-200">
              <div className="bg-indigo-50 px-4 py-2 text-xs font-bold uppercase tracking-wide text-indigo-800">Matter details</div>
              <dl className="divide-y divide-gray-100">
                {[
                  ['Debtor(s)', draft.debtorNames],
                  ['Garnishing creditor', draft.creditorName],
                  ['Civil lawsuit', draft.civilCaseTitle],
                  ['Civil case number', draft.civilCaseNumber],
                  ['Civil court', draft.civilCourtName],
                  ['Bankruptcy case number', draft.bankruptcyCaseNumber],
                  ['Bankruptcy filing', `Chapter ${draft.chapter || '—'}${draft.dateFiled ? `, filed ${dateLabel(draft.dateFiled)}` : ''}`],
                  ['Deadline before adversary proceeding', draft.adversaryDeadline],
                ].filter(([, value]) => value).map(([label, value]) => (
                  <div key={label} className="grid grid-cols-[minmax(120px,38%)_1fr] gap-3 px-4 py-2.5">
                    <dt className="text-gray-500">{label}</dt>
                    <dd className="font-semibold text-gray-900">{value}</dd>
                  </div>
                ))}
              </dl>
            </div>

            <div>
              <h4 className="mb-2 font-semibold text-gray-900">Garnishment activity</h4>
              <div className="overflow-x-auto">
                <table className="min-w-full border-collapse border border-gray-300 text-left text-xs">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr><th className="border-b border-gray-300 px-3 py-2">Date</th><th className="border-b border-gray-300 px-3 py-2">Description</th><th className="border-b border-gray-300 px-3 py-2 text-right">Amount</th></tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {garnishments.map((item, index) => (
                      <tr key={item.id || `${item.date_iso}-${index}`}>
                        <td className="whitespace-nowrap px-3 py-2.5">{item.date || dateLabel(item.date_iso)}</td>
                        <td className="px-3 py-2.5">{item.description || 'Wage garnishment'}</td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-right font-semibold">{item.amount || money(item.amount_value)}</td>
                      </tr>
                    ))}
                    {!garnishments.length && <tr><td colSpan="3" className="px-3 py-3 text-gray-500">No garnishment entries were saved with this packet.</td></tr>}
                  </tbody>
                </table>
              </div>
              <dl className="mt-3 divide-y divide-gray-200 rounded-lg border border-gray-200 bg-gray-50">
                <div className="flex justify-between px-4 py-2"><dt className="text-gray-500">Total garnished</dt><dd className="font-bold text-gray-900">{money(totalGarnished)}</dd></div>
                <div className="flex justify-between px-4 py-2"><dt className="text-gray-500">Amount already returned</dt><dd className="font-bold text-emerald-700">{money(returned)}</dd></div>
                <div className="flex justify-between px-4 py-2.5"><dt className="font-bold text-gray-900">Amount remaining to be returned</dt><dd className="font-bold text-red-700">{money(remaining)}</dd></div>
              </dl>
            </div>

            <div className="rounded-md border border-emerald-200 border-l-4 border-l-emerald-600 bg-emerald-50 px-4 py-3">
              <p className="text-xs font-bold uppercase tracking-wide text-emerald-700">Remit returned garnished funds to</p>
              <p className="mt-2 font-bold text-emerald-900">{draft.trustee?.name || 'Trustee information not available'}</p>
              <p className="mt-1 text-sm leading-5 text-emerald-800">
                {draft.trustee?.address1 && <>{draft.trustee.address1}<br /></>}
                {draft.trustee?.address2 && <>{draft.trustee.address2}<br /></>}
                {[draft.trustee?.city, draft.trustee?.state, draft.trustee?.zip].filter(Boolean).join(', ').replace(/, ([A-Z]{2}), /, '$1 ')}
              </p>
            </div>

            <div className="rounded border-l-4 border-amber-400 bg-amber-50 px-4 py-3 text-xs leading-5 text-amber-900">
              The complete demand, Notice of Automatic Stay, bankruptcy notice, and supporting garnishment documents are attached as a single PDF.
            </div>

            <div className="border-t-4 border-indigo-500 bg-gray-50 p-4">
              <div className="flex items-start gap-4">
                <img src="https://cisowskilaw.com/wp-content/uploads/2020/05/cropped-output-onlinepngtools-1-copy-63x63.png" alt="Cisowski Law Logo" className="h-12 w-12 rounded" />
                <div>
                  <p className="font-bold text-gray-900">Oliver Cisowski</p>
                  <p className="text-gray-900">Cisowski Law</p>
                  <p className="mt-1 text-xs leading-5 text-gray-500">3119 Bristol Hwy Ste 217<br />Johnson City, TN 37601<br /><strong>Phone:</strong> (423) 342-4722<br /><strong>Email:</strong> oliver@cisowskilaw.com</p>
                  <p className="mt-2 text-xs italic text-gray-700">“The financial peace of mind attorney”</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function GarnishmentPacketPage() {
  const { caseId } = useParams();
  const token = useMemo(
    () => localStorage.getItem('accessToken') || localStorage.getItem('token') || '',
    [],
  );

  const [context, setContext] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savingReturnedAmount, setSavingReturnedAmount] = useState(false);
  const [deletingPacketId, setDeletingPacketId] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [returnedAmountMessage, setReturnedAmountMessage] = useState('');
  const [emailComposer, setEmailComposer] = useState(null);
  const [emailError, setEmailError] = useState('');
  const [emailSuccess, setEmailSuccess] = useState('');

  const [lawsuitId, setLawsuitId] = useState('');
  const [garnishingCreditorId, setGarnishingCreditorId] = useState('');
  const [garnishmentIds, setGarnishmentIds] = useState([]);
  const [recipientChoices, setRecipientChoices] = useState({});
  const [filingNoticeSource, setFilingNoticeSource] = useState('docket');
  const [docketDocumentId, setDocketDocumentId] = useState('');
  const [filingNoticeFile, setFilingNoticeFile] = useState(null);
  const [paystubs, setPaystubs] = useState([]);
  const [attorneyId, setAttorneyId] = useState('');
  const [trustee, setTrustee] = useState({
    name: '', address1: '', address2: '', city: '', state: '', zip: '',
  });
  const [serveTrustee, setServeTrustee] = useState(false);
  const [recoveredAmount, setRecoveredAmount] = useState('0.00');
  const [demandDueDate, setDemandDueDate] = useState(defaultDemandDate);
  const [serviceMethod, setServiceMethod] = useState('United States mail, postage prepaid');
  const [additionalText, setAdditionalText] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function loadContext() {
      setLoading(true);
      setError('');
      try {
        const response = await fetch(`${API_URL}/case/${caseId}/garnishment-packet/context/`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
        });
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(payload.error || 'Could not load garnishment records.');
        if (cancelled) return;
        setContext(payload);
        setLawsuitId(payload.lawsuits?.[0]?.id ? String(payload.lawsuits[0].id) : '');
        setDocketDocumentId(
          payload.recommended_docket_source_id
            ? String(payload.recommended_docket_source_id)
            : payload.docket_documents?.[0]?.source_id
              ? String(payload.docket_documents[0].source_id)
              : '',
        );
        setAttorneyId(payload.attorneys?.[0]?.id ? String(payload.attorneys[0].id) : '');
        setDemandDueDate(defaultDemandDate(payload.case));
        setTrustee({
          name: payload.case?.trustee?.name || '',
          address1: payload.case?.trustee?.address1 || '',
          address2: payload.case?.trustee?.address2 || '',
          city: payload.case?.trustee?.city || '',
          state: payload.case?.trustee?.state || '',
          zip: payload.case?.trustee?.zip || '',
        });
      } catch (loadError) {
        if (!cancelled) setError(loadError.message || 'Could not load this page.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    if (caseId) loadContext();
    return () => { cancelled = true; };
  }, [caseId, token]);

  const selectedLawsuit = useMemo(
    () => context?.lawsuits?.find((item) => String(item.id) === lawsuitId),
    [context, lawsuitId],
  );
  const selectedCreditor = useMemo(
    () => context?.creditors?.find((item) => String(item.id) === garnishingCreditorId),
    [context, garnishingCreditorId],
  );
  const matchingGarnishments = useMemo(
    () => (context?.garnishments || []).filter(
      (item) => String(item.creditor_id) === garnishingCreditorId,
    ),
    [context, garnishingCreditorId],
  );
  const creditorGarnishmentTotals = useMemo(
    () => (context?.garnishments || []).reduce((totals, item) => {
      const creditorId = String(item.creditor_id);
      const current = totals[creditorId] || { amount: 0, count: 0 };
      totals[creditorId] = {
        amount: current.amount + Number(item.amount_value || 0),
        count: current.count + 1,
      };
      return totals;
    }, {}),
    [context],
  );
  const orderedGarnishingCreditors = useMemo(
    () => [...(context?.creditors || [])].sort((left, right) => {
      const leftSummary = creditorGarnishmentTotals[String(left.id)] || { amount: 0, count: 0 };
      const rightSummary = creditorGarnishmentTotals[String(right.id)] || { amount: 0, count: 0 };
      return rightSummary.amount - leftSummary.amount
        || rightSummary.count - leftSummary.count
        || String(left.name || '').localeCompare(String(right.name || ''));
    }),
    [context, creditorGarnishmentTotals],
  );
  const additionalCreditors = useMemo(
    () => (context?.creditors || []).filter(
      (creditor) => String(creditor.id) !== garnishingCreditorId,
    ),
    [context, garnishingCreditorId],
  );
  const selectedGarnishments = useMemo(
    () => matchingGarnishments.filter((item) => garnishmentIds.includes(String(item.id))),
    [matchingGarnishments, garnishmentIds],
  );
  const selectedTotal = selectedGarnishments.reduce(
    (total, item) => total + Number(item.amount_value || 0),
    0,
  );
  const additionalRecipients = useMemo(
    () => Object.entries(recipientChoices)
      .filter(([, choice]) => choice.notice || choice.demand)
      .map(([creditorId, choice]) => ({
        creditor_id: creditorId,
        include_notice: Boolean(choice.notice),
        include_demand: Boolean(choice.demand),
      })),
    [recipientChoices],
  );

  function chooseGarnishingCreditor(value) {
    setGarnishingCreditorId(value);
    const creditor = (context?.creditors || []).find((item) => String(item.id) === value);
    setRecoveredAmount(creditor?.garnishment_returned_amount || '0.00');
    setReturnedAmountMessage('');
    const ids = (context?.garnishments || [])
      .filter((item) => String(item.creditor_id) === value)
      .map((item) => String(item.id));
    setGarnishmentIds(ids);
    setRecipientChoices((current) => {
      const next = { ...current };
      delete next[value];
      return next;
    });
  }

  function toggleGarnishment(id) {
    const key = String(id);
    setGarnishmentIds((current) => (
      current.includes(key) ? current.filter((value) => value !== key) : [...current, key]
    ));
  }

  function toggleRecipient(creditorId, kind) {
    const key = String(creditorId);
    setRecipientChoices((current) => ({
      ...current,
      [key]: {
        notice: Boolean(current[key]?.notice),
        demand: Boolean(current[key]?.demand),
        [kind]: !current[key]?.[kind],
      },
    }));
  }

  async function saveReturnedAmount() {
    if (!garnishingCreditorId) {
      setError('Select the garnishing creditor before saving the returned amount.');
      return;
    }
    const amount = Number(recoveredAmount);
    if (!Number.isFinite(amount) || amount < 0) {
      setError('Amount already returned must be a valid non-negative dollar amount.');
      return;
    }
    setSavingReturnedAmount(true);
    setReturnedAmountMessage('');
    setError('');
    try {
      const response = await fetch(
        `${API_URL}/case/${caseId}/garnishment-packet/creditors/${garnishingCreditorId}/returned-amount/`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
          body: JSON.stringify({ amount: recoveredAmount || '0' }),
        },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'The returned amount could not be saved.');
      const savedAmount = payload.garnishment_returned_amount || '0.00';
      setRecoveredAmount(savedAmount);
      setContext((current) => current ? {
        ...current,
        creditors: (current.creditors || []).map((creditor) => (
          String(creditor.id) === garnishingCreditorId
            ? { ...creditor, garnishment_returned_amount: savedAmount }
            : creditor
        )),
      } : current);
      setReturnedAmountMessage('Returned amount saved.');
    } catch (saveError) {
      setError(saveError.message || 'The returned amount could not be saved.');
    } finally {
      setSavingReturnedAmount(false);
    }
  }

  async function deletePacket(generation) {
    const confirmed = window.confirm(
      'Delete this packet, its stored ZIP, and its service todo? Files already saved in OneDrive will remain.',
    );
    if (!confirmed) return;
    setDeletingPacketId(String(generation.id));
    setError('');
    try {
      const response = await fetch(
        `${API_URL}/case/${caseId}/garnishment-packet/${generation.id}/`,
        {
          method: 'DELETE',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
          credentials: 'include',
        },
      );
      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.error || 'The packet could not be deleted.');
      }
      setContext((current) => current ? {
        ...current,
        generations: (current.generations || []).filter(
          (item) => String(item.id) !== String(generation.id),
        ),
      } : current);
      if (String(result?.id || '') === String(generation.id)) setResult(null);
    } catch (deleteError) {
      setError(deleteError.message || 'The packet could not be deleted.');
    } finally {
      setDeletingPacketId('');
    }
  }

  function openEmailComposer(generation) {
    const attachments = emailAttachmentOptions(generation);
    if (!attachments.length) {
      setError('This packet does not have a service-packet PDF available to email.');
      return;
    }
    const primaryAttachment = attachments.find(
      (item) => item.recipient_name
        && item.recipient_name === generation.garnishing_creditor_name,
    ) || attachments[0];
    const debtorNames = filingDebtors(context.case?.debtors)
      .map((debtor) => debtor.name || debtorFullName(debtor))
      .join(' and ');
    const subjectName = debtorNames || 'Debtor';
    const bankruptcyCaseNumber = context.case?.case_number || 'Not available';
    const civilCaseNumber = generation.civil_case_number || 'Not available';
    const calculatedAdversaryDeadline = longDate(
      adversaryDeadline(context.case?.meeting_date),
    );
    setEmailError('');
    setEmailSuccess('');
    setEmailComposer({
      packetId: generation.id,
      attachments,
      to: '',
      cc: '',
      subject: `Demand for Return of Garnished Funds — Civil Case No. ${civilCaseNumber} — Bankruptcy Case No. ${bankruptcyCaseNumber}`,
      body: `Please see the attached demand for the return of garnished funds and Notice of Automatic Stay concerning ${subjectName}. This matter relates to Civil Case No. ${civilCaseNumber} and Bankruptcy Case No. ${bankruptcyCaseNumber}.\n\nThe garnishment activity and amount remaining to be returned are summarized below. Unless the remaining garnished funds are remitted to the trustee on or before ${calculatedAdversaryDeadline}, we will commence an adversary proceeding to recover the funds and seek all other available relief, without further notice.`,
      attachment_filename: primaryAttachment.filename,
      debtorNames: subjectName,
      creditorName: generation.garnishing_creditor_name || 'Garnishing creditor',
      civilCaseNumber,
      civilCaseTitle: generation.civil_case_title || '',
      civilCourtName: generation.civil_court_name || '',
      bankruptcyCaseNumber,
      chapter: context.case?.chapter || '',
      dateFiled: context.case?.date_filed || '',
      adversaryDeadline: calculatedAdversaryDeadline,
      garnishments: generation.garnishment_snapshot || [],
      recoveredAmount: generation.recovered_amount || '0',
      trustee: context.case?.trustee || {},
    });
  }

  async function sendPacketEmail(event) {
    event.preventDefault();
    if (!emailComposer) return;
    setSendingEmail(true);
    setEmailError('');
    try {
      const response = await fetch(
        `${API_URL}/case/${caseId}/garnishment-packet/${emailComposer.packetId}/email/`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: 'include',
          body: JSON.stringify({
            to: emailComposer.to,
            cc: emailComposer.cc,
            subject: emailComposer.subject,
            body: emailComposer.body,
            attachment_filename: emailComposer.attachment_filename,
          }),
        },
      );
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'The email could not be sent.');
      setEmailSuccess(`Email sent to ${(payload.to || []).join(', ')}.`);
      setEmailComposer(null);
    } catch (sendError) {
      setEmailError(sendError.message || 'The email could not be sent.');
    } finally {
      setSendingEmail(false);
    }
  }

  function validate() {
    if (!context.case?.case_number) return 'Add the bankruptcy case number before generating.';
    if (!context.case?.date_filed) return 'Add the bankruptcy filing date before generating.';
    if (!(context.case?.debtors || []).some((debtor) => debtor.is_filing)) return 'Mark at least one debtor as filing before generating.';
    if (!lawsuitId) return 'Select the state-court lawsuit.';
    if (selectedLawsuit && !selectedLawsuit.case_number) return 'Add the civil case number to the selected lawsuit.';
    if (selectedLawsuit && (!selectedLawsuit.court_name || !selectedLawsuit.court_street || !selectedLawsuit.court_city || !selectedLawsuit.court_state || !selectedLawsuit.court_zip)) {
      return 'Complete the selected lawsuit’s court name and mailing address before generating.';
    }
    if (!garnishingCreditorId) return 'Select the garnishing creditor.';
    if (selectedCreditor && (!selectedCreditor.address1 || !selectedCreditor.city || !selectedCreditor.state || !selectedCreditor.zip)) {
      return 'Complete the garnishing creditor’s mailing address before generating.';
    }
    if (!garnishmentIds.length) return 'Select at least one garnishment record.';
    if (filingNoticeSource === 'docket' && !docketDocumentId) {
      return 'Select the bankruptcy filing notice from the docket or upload it.';
    }
    if (filingNoticeSource === 'upload' && !filingNoticeFile) {
      return 'Choose a PDF of the bankruptcy filing notice.';
    }
    if (!trustee.name) return 'Enter the trustee name for the demand letter.';
    if (!trustee.address1 || !trustee.city || !trustee.state || !trustee.zip) {
      return 'Complete the trustee mailing address for the demand letter.';
    }
    return '';
  }

  async function generatePacket(event) {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSubmitting(true);
    setError('');
    setResult(null);
    try {
      const form = new FormData();
      form.append('lawsuit_id', lawsuitId);
      form.append('garnishing_creditor_id', garnishingCreditorId);
      form.append('garnishment_ids', JSON.stringify(garnishmentIds));
      form.append('service_recipients', JSON.stringify(additionalRecipients));
      form.append('trustee', JSON.stringify(trustee));
      form.append('serve_trustee', String(serveTrustee));
      form.append('recovered_amount', recoveredAmount || '0');
      form.append('demand_due_date', demandDueDate);
      form.append('service_method', serviceMethod);
      form.append('additional_text', additionalText);
      if (attorneyId) form.append('attorney_id', attorneyId);
      if (filingNoticeSource === 'upload') {
        form.append('filing_notice', filingNoticeFile);
      } else {
        form.append('docket_source_id', docketDocumentId);
      }
      paystubs.forEach((file) => form.append('paystubs', file));

      const response = await fetch(`${API_URL}/case/${caseId}/garnishment-packet/generate/`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
        body: form,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.error || 'The garnishment packet could not be generated.');
      setResult(payload);
      const persistedReturnedAmount = payload.recovered_amount || recoveredAmount || '0.00';
      setRecoveredAmount(persistedReturnedAmount);
      setReturnedAmountMessage('Returned amount saved.');
      setContext((current) => current ? {
        ...current,
        creditors: (current.creditors || []).map((creditor) => (
          String(creditor.id) === garnishingCreditorId
            ? { ...creditor, garnishment_returned_amount: persistedReturnedAmount }
            : creditor
        )),
        generations: [{
          id: payload.id,
          status: payload.status,
          created_at: new Date().toISOString(),
          archive_url: payload.archive_url,
          onedrive_files: payload.files || [],
          garnishing_creditor_name: selectedCreditor?.name || '',
          civil_case_number: selectedLawsuit?.case_number || '',
          civil_case_title: selectedLawsuit?.case_title || '',
          civil_court_name: selectedLawsuit?.court_name || '',
          garnishment_snapshot: selectedGarnishments,
          recovered_amount: persistedReturnedAmount,
          served_at: payload.served_at || null,
          error_message: (payload.warnings || []).join('\n'),
        }, ...(current.generations || [])].slice(0, 10),
      } : current);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (submitError) {
      setError(submitError.message || 'The garnishment packet could not be generated.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-64 items-center justify-center text-sm text-gray-500">
        <ArrowPathIcon className="mr-2 h-5 w-5 animate-spin" /> Loading garnishment records…
      </div>
    );
  }

  if (!context) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
        {error || 'This case could not be loaded.'}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl pb-16">
      <div className="mb-7">
        <p className="text-sm font-medium text-indigo-600">Letters and notices</p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-gray-900">
          Garnishment Notice &amp; Demand
        </h1>
        <p className="mt-2 max-w-3xl text-sm text-gray-600">
          Create the state-court notice, return demand, service packets, and envelopes from the case records.
          The completed files are saved to the client’s OneDrive folder.
        </p>
      </div>

      {error && (
        <div className="mb-5 flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          <ExclamationTriangleIcon className="h-5 w-5 flex-none" />
          <span>{error}</span>
        </div>
      )}

      {emailSuccess && (
        <div className="mb-5 flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
          <CheckCircleIcon className="h-5 w-5 flex-none" />
          <span>{emailSuccess}</span>
        </div>
      )}

      {result && (
        <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-5">
          <div className="flex items-start gap-3">
            <CheckCircleIcon className="h-6 w-6 flex-none text-emerald-600" />
            <div className="min-w-0 flex-1">
              <h2 className="font-semibold text-emerald-900">Packet generated</h2>
              <p className="mt-1 text-sm text-emerald-800">
                Status: {result.status}. The files were assembled and a service-document todo was created.
              </p>
              <div className="mt-3 flex flex-wrap gap-3">
                {result.onedrive_folder_url && (
                  <a className="inline-flex items-center rounded-md bg-emerald-700 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-800" href={result.onedrive_folder_url} target="_blank" rel="noreferrer">
                    <FolderOpenIcon className="mr-2 h-4 w-4" /> Open OneDrive folder
                  </a>
                )}
                {result.archive_url && (
                  <a className="inline-flex items-center rounded-md border border-emerald-300 bg-white px-3 py-2 text-sm font-semibold text-emerald-800 hover:bg-emerald-100" href={result.archive_url} target="_blank" rel="noreferrer">
                    Download complete ZIP
                  </a>
                )}
              </div>
              {(result.warnings || []).map((warning) => (
                <p key={warning} className="mt-3 text-sm font-medium text-amber-800">{warning}</p>
              ))}
            </div>
          </div>
        </div>
      )}

      <form onSubmit={generatePacket} className="space-y-5">
        <Section
          number="1"
          title="Choose the lawsuit"
          description="The caption, civil case number, and court address will populate the notice."
        >
          <label className="block text-sm font-medium text-gray-700">
            Lawsuit record
            <select className={inputClass} value={lawsuitId} onChange={(event) => setLawsuitId(event.target.value)}>
              <option value="">Select a lawsuit</option>
              {(context.lawsuits || []).map((lawsuit) => (
                <option key={lawsuit.id} value={lawsuit.id}>
                  {lawsuit.case_title || 'Untitled lawsuit'}{lawsuit.case_number ? ` — ${lawsuit.case_number}` : ''}
                </option>
              ))}
            </select>
          </label>
          {!context.lawsuits?.length && (
            <p className="mt-3 text-sm text-amber-700">No lawsuits are recorded in the Statement of Financial Affairs.</p>
          )}
          {selectedLawsuit && (
            <dl className="mt-4 grid gap-3 rounded-lg bg-gray-50 p-4 text-sm sm:grid-cols-2">
              <div><dt className="font-medium text-gray-500">Court</dt><dd className="mt-1 text-gray-900">{selectedLawsuit.court_name || 'Not entered'}</dd></div>
              <div><dt className="font-medium text-gray-500">Court address</dt><dd className="mt-1 text-gray-900">{[selectedLawsuit.court_street, selectedLawsuit.court_city, selectedLawsuit.court_state, selectedLawsuit.court_zip].filter(Boolean).join(', ') || 'Not entered'}</dd></div>
            </dl>
          )}
        </Section>

        <Section
          number="2"
          title="Choose the garnishing creditor and entries"
          description="The garnishing creditor automatically receives both the notice and the demand."
        >
          <label className="block text-sm font-medium text-gray-700">
            Garnishing creditor
            <select className={inputClass} value={garnishingCreditorId} onChange={(event) => chooseGarnishingCreditor(event.target.value)}>
              <option value="">Select a creditor</option>
              {orderedGarnishingCreditors.map((creditor) => {
                const summary = creditorGarnishmentTotals[String(creditor.id)] || { amount: 0, count: 0 };
                return (
                  <option key={creditor.id} value={creditor.id}>
                    {creditor.name} — {money(summary.amount)} total ({summary.count} {summary.count === 1 ? 'entry' : 'entries'})
                  </option>
                );
              })}
            </select>
          </label>
          {garnishingCreditorId && !matchingGarnishments.length && (
            <p className="mt-3 text-sm text-amber-700">This creditor has no garnishment entries in the SOFA records.</p>
          )}
          {matchingGarnishments.length > 0 && (
            <div className="mt-4 overflow-hidden rounded-lg border border-gray-200">
              <div className="grid grid-cols-[auto_1fr_auto] gap-3 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                <span /> <span>Garnishment record</span> <span>Amount</span>
              </div>
              {matchingGarnishments.map((item) => (
                <label key={item.id} className="grid cursor-pointer grid-cols-[auto_1fr_auto] items-center gap-3 border-t border-gray-100 px-4 py-3 text-sm hover:bg-indigo-50">
                  <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" checked={garnishmentIds.includes(String(item.id))} onChange={() => toggleGarnishment(item.id)} />
                  <span><span className="font-medium text-gray-900">{dateLabel(item.date_iso)}</span><span className="block text-gray-500">{item.description}</span></span>
                  <span className="font-semibold text-gray-900">{item.amount}</span>
                </label>
              ))}
              <div className="flex justify-end border-t border-gray-200 bg-gray-50 px-4 py-3 text-sm font-semibold text-gray-900">Selected total: {money(selectedTotal)}</div>
            </div>
          )}
        </Section>

        <Section
          number="3"
          title="Choose additional people to serve"
          description="Select whether each additional case creditor receives the notice, a demand, or both."
        >
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <div className="grid grid-cols-[1fr_72px_72px] gap-2 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              <span>Creditor</span><span className="text-center">Notice</span><span className="text-center">Demand</span>
            </div>
            <div className="max-h-[30rem] overflow-y-auto">
              {additionalCreditors.map((creditor) => (
                <div key={creditor.id} className="grid grid-cols-[1fr_72px_72px] items-center gap-2 border-t border-gray-100 px-4 py-3 text-sm">
                  <div className="min-w-0"><p className="truncate font-medium text-gray-900">{creditor.name}</p><p className="truncate text-xs text-gray-500">{[creditor.address1, creditor.city, creditor.state, creditor.zip].filter(Boolean).join(', ') || 'Address incomplete'}</p></div>
                  <div className="text-center"><input aria-label={`Serve notice on ${creditor.name}`} type="checkbox" className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" checked={Boolean(recipientChoices[String(creditor.id)]?.notice)} onChange={() => toggleRecipient(creditor.id, 'notice')} /></div>
                  <div className="text-center"><input aria-label={`Serve demand on ${creditor.name}`} type="checkbox" className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" checked={Boolean(recipientChoices[String(creditor.id)]?.demand)} onChange={() => toggleRecipient(creditor.id, 'demand')} /></div>
                </div>
              ))}
              {!additionalCreditors.length && <p className="border-t border-gray-100 p-4 text-sm text-gray-500">No additional case creditors are available.</p>}
            </div>
          </div>
          {selectedCreditor && (
            <p className="mt-3 text-xs text-gray-500">{selectedCreditor.name} is already included as the primary notice and demand recipient.</p>
          )}
        </Section>

        <Section
          number="4"
          title="Add the bankruptcy filing notice"
          description="Use the recommended docket document or replace it with an uploaded PDF."
        >
          <div className="flex flex-wrap gap-5 text-sm">
            <label className="flex items-center gap-2"><input type="radio" name="filing-source" value="docket" checked={filingNoticeSource === 'docket'} onChange={() => setFilingNoticeSource('docket')} /> From docket</label>
            <label className="flex items-center gap-2"><input type="radio" name="filing-source" value="upload" checked={filingNoticeSource === 'upload'} onChange={() => setFilingNoticeSource('upload')} /> Upload PDF</label>
          </div>
          {filingNoticeSource === 'docket' ? (
            <label className="mt-4 block text-sm font-medium text-gray-700">
              Docket document
              <select className={inputClass} value={docketDocumentId} onChange={(event) => setDocketDocumentId(event.target.value)}>
                <option value="">Select a docket document</option>
                {(context.docket_documents || []).map((document) => {
                  const sourceId = String(document.source_id || document.id);
                  const isRecommended = sourceId === String(context.recommended_docket_source_id || '');
                  return (
                    <option key={sourceId} value={sourceId}>
                      {isRecommended ? 'Recommended — ' : ''}{docketEntryLabel(document)}
                    </option>
                  );
                })}
              </select>
            </label>
          ) : (
            <label className="mt-4 block text-sm font-medium text-gray-700">
              Filing notice PDF
              <input className={inputClass} type="file" accept="application/pdf,.pdf" onChange={(event) => setFilingNoticeFile(event.target.files?.[0] || null)} />
            </label>
          )}
        </Section>

        <Section
          number="5"
          title="Add garnished paystubs"
          description="Upload one or more PDF files. They will be copied into each service packet."
        >
          <label className="flex cursor-pointer items-center justify-center rounded-lg border-2 border-dashed border-gray-300 px-6 py-8 text-center hover:border-indigo-400 hover:bg-indigo-50">
            <span><DocumentArrowUpIcon className="mx-auto h-8 w-8 text-gray-400" /><span className="mt-2 block text-sm font-semibold text-gray-800">Choose paystub PDFs</span><span className="mt-1 block text-xs text-gray-500">Multiple files are allowed</span></span>
            <input className="sr-only" type="file" multiple accept="application/pdf,.pdf" onChange={(event) => setPaystubs(Array.from(event.target.files || []))} />
          </label>
          {paystubs.length > 0 && (
            <ul className="mt-3 space-y-1 text-sm text-gray-700">{paystubs.map((file) => <li key={`${file.name}-${file.size}`}>{file.name}</li>)}</ul>
          )}
        </Section>

        <Section
          number="6"
          title="Review attorney, trustee, and demand details"
          description="Trustee changes entered here are saved back to the case for future use."
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block text-sm font-medium text-gray-700">Signing attorney<select className={inputClass} value={attorneyId} onChange={(event) => setAttorneyId(event.target.value)}><option value="">Use firm information</option>{(context.attorneys || []).map((attorney) => <option key={attorney.id} value={attorney.id}>{attorney.name}{attorney.bar_number ? ` — ${attorney.bar_number}` : ''}</option>)}</select></label>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Amount already returned
                <input className={inputClass} type="number" min="0" step="0.01" value={recoveredAmount} onChange={(event) => { setRecoveredAmount(event.target.value); setReturnedAmountMessage(''); }} />
              </label>
              <div className="mt-2 flex items-center gap-3">
                <button type="button" disabled={!garnishingCreditorId || savingReturnedAmount} onClick={saveReturnedAmount} className="rounded-md border border-indigo-300 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50">
                  {savingReturnedAmount ? 'Saving…' : 'Save returned amount'}
                </button>
                {returnedAmountMessage && <span className="text-xs font-medium text-emerald-700">{returnedAmountMessage}</span>}
              </div>
              <p className="mt-1 text-xs text-gray-500">Saved to the selected creditor and loaded automatically next time.</p>
            </div>
            <label className="block text-sm font-medium text-gray-700">Requested response / return date<input className={inputClass} type="date" value={demandDueDate} onChange={(event) => setDemandDueDate(event.target.value)} /></label>
            <label className="block text-sm font-medium text-gray-700 sm:col-span-2">Certificate service method<input className={inputClass} value={serviceMethod} onChange={(event) => setServiceMethod(event.target.value)} /></label>
          </div>

          <div className="mt-6 border-t border-gray-200 pt-5">
            <div className="flex items-center justify-between gap-4"><div><h3 className="text-sm font-semibold text-gray-900">Trustee / remittance information</h3><p className="mt-1 text-xs text-gray-500">Included in the demand letter.</p></div><label className="flex items-center gap-2 text-sm font-medium text-gray-700"><input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-indigo-600" checked={serveTrustee} onChange={(event) => setServeTrustee(event.target.checked)} /> Also serve notice</label></div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              {[['name', 'Trustee name'], ['address1', 'Address line 1'], ['address2', 'Address line 2'], ['city', 'City'], ['state', 'State'], ['zip', 'ZIP code']].map(([key, label]) => (
                <label key={key} className="block text-sm font-medium text-gray-700">{label}<input className={inputClass} value={trustee[key]} onChange={(event) => setTrustee((current) => ({ ...current, [key]: event.target.value }))} /></label>
              ))}
            </div>
          </div>

          <label className="mt-6 block text-sm font-medium text-gray-700">
            Optional closing paragraph
            <textarea className={inputClass} rows="3" value={additionalText} onChange={(event) => setAdditionalText(event.target.value)} placeholder="Please contact our office promptly with any questions." />
          </label>
        </Section>

        <div className="sticky bottom-4 rounded-xl border border-gray-200 bg-white/95 p-4 shadow-lg backdrop-blur">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-gray-600">
              <span className="font-semibold text-gray-900">Ready to create:</span>{' '}
              notice + {1 + additionalRecipients.filter((item) => item.include_demand).length} demand letter(s),{' '}
              {1 + additionalRecipients.length + (serveTrustee ? 1 : 0)} service packet(s), and envelopes.
            </div>
            <button type="submit" disabled={submitting} className="inline-flex min-w-48 items-center justify-center rounded-md bg-indigo-600 px-5 py-3 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60">
              {submitting ? <><ArrowPathIcon className="mr-2 h-5 w-5 animate-spin" /> Creating packet…</> : 'Generate and save packet'}
            </button>
          </div>
        </div>
      </form>

      {(context.generations || []).length > 0 && (
        <section className="mt-8">
          <h2 className="text-base font-semibold text-gray-900">Recent packet history</h2>
          <div className="mt-3 overflow-hidden rounded-lg border border-gray-200 bg-white">
            {context.generations.map((generation) => (
              <div key={generation.id} className="flex flex-col gap-2 border-b border-gray-100 px-4 py-3 text-sm last:border-0 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <span className="font-medium text-gray-900">{new Date(generation.created_at).toLocaleString()}</span>
                  <span className="ml-2 rounded-full bg-gray-100 px-2 py-0.5 text-xs capitalize text-gray-700">{generation.status}</span>
                  {generation.recovered_amount != null && <p className="mt-1 text-xs text-gray-500">Amount returned at generation: {money(generation.recovered_amount)}</p>}
                  <p className={`mt-1 text-xs font-semibold ${generation.served_at ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {generation.served_at ? `Service completed ${new Date(generation.served_at).toLocaleString()}` : 'Awaiting service'}
                  </p>
                  {generation.error_message && <p className="mt-1 text-xs text-amber-700">{generation.error_message}</p>}
                </div>
                <div className="flex items-center gap-4">
                  {generation.archive_url && <a className="font-semibold text-indigo-600 hover:text-indigo-800" href={generation.archive_url} target="_blank" rel="noreferrer">Download ZIP</a>}
                  <button
                    type="button"
                    disabled={!emailAttachmentOptions(generation).length}
                    onClick={() => openEmailComposer(generation)}
                    className="inline-flex items-center font-semibold text-indigo-600 hover:text-indigo-800 disabled:cursor-not-allowed disabled:text-gray-400"
                    title={emailAttachmentOptions(generation).length ? 'Compose an email with the service packet attached' : 'No service-packet PDF is available'}
                  >
                    <EnvelopeIcon className="mr-1 h-4 w-4" />
                    Email packet
                  </button>
                  <button type="button" disabled={deletingPacketId === String(generation.id)} onClick={() => deletePacket(generation)} className="inline-flex items-center font-semibold text-red-600 hover:text-red-800 disabled:cursor-not-allowed disabled:opacity-50">
                    <TrashIcon className="mr-1 h-4 w-4" />
                    {deletingPacketId === String(generation.id) ? 'Deleting…' : 'Delete packet'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {emailComposer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4" role="dialog" aria-modal="true" aria-labelledby="packet-email-title">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-gray-200 px-5 py-4 sm:px-6">
              <div>
                <h2 id="packet-email-title" className="text-lg font-semibold text-gray-900">Email garnishment packet</h2>
                <p className="mt-1 text-sm text-gray-500">Review the message, garnishment details, and recipient before sending.</p>
              </div>
              <button type="button" disabled={sendingEmail} onClick={() => setEmailComposer(null)} className="rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Close email composer">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={sendPacketEmail} className="space-y-4 px-5 py-5 sm:px-6">
              {emailError && (
                <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                  <ExclamationTriangleIcon className="h-5 w-5 flex-none" />
                  <span>{emailError}</span>
                </div>
              )}
              <label className="block text-sm font-medium text-gray-700">
                To
                <input
                  className={inputClass}
                  type="text"
                  value={emailComposer.to}
                  onChange={(event) => setEmailComposer((current) => ({ ...current, to: event.target.value }))}
                  placeholder="recipient@example.com"
                  autoFocus
                  required
                />
                <span className="mt-1 block text-xs font-normal text-gray-500">Separate multiple addresses with commas or semicolons.</span>
              </label>
              <label className="block text-sm font-medium text-gray-700">
                CC
                <input
                  className={inputClass}
                  type="text"
                  value={emailComposer.cc}
                  onChange={(event) => setEmailComposer((current) => ({ ...current, cc: event.target.value }))}
                  placeholder="Optional"
                />
              </label>
              <label className="block text-sm font-medium text-gray-700">
                Subject
                <input
                  className={inputClass}
                  type="text"
                  maxLength="255"
                  value={emailComposer.subject}
                  onChange={(event) => setEmailComposer((current) => ({ ...current, subject: event.target.value }))}
                  required
                />
              </label>
              <label className="block text-sm font-medium text-gray-700">
                Introductory message
                <textarea
                  className={inputClass}
                  rows="8"
                  maxLength="50000"
                  value={emailComposer.body}
                  onChange={(event) => setEmailComposer((current) => ({ ...current, body: event.target.value }))}
                  required
                />
                <span className="mt-1 block text-xs font-normal text-gray-500">The matter details, garnishment table, totals, attachment notice, and signature block are added automatically.</span>
              </label>
              <label className="block text-sm font-medium text-gray-700">
                Attached service packet
                <select
                  className={inputClass}
                  value={emailComposer.attachment_filename}
                  onChange={(event) => setEmailComposer((current) => ({ ...current, attachment_filename: event.target.value }))}
                  required
                >
                  {emailComposer.attachments.map((attachment) => (
                    <option key={attachment.filename} value={attachment.filename}>
                      {attachment.recipient_name || 'Service packet'} — {attachment.filename.split('/').pop()}
                    </option>
                  ))}
                </select>
                <span className="mt-1 block text-xs font-normal text-gray-500">The PDF includes the demand and the other documents assembled for that recipient.</span>
              </label>

              <EmailPreview draft={emailComposer} />

              <div className="flex flex-col-reverse gap-3 border-t border-gray-200 pt-4 sm:flex-row sm:justify-end">
                <button type="button" disabled={sendingEmail} onClick={() => setEmailComposer(null)} className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">Cancel</button>
                <button type="submit" disabled={sendingEmail} className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-60">
                  {sendingEmail ? <><ArrowPathIcon className="mr-2 h-5 w-5 animate-spin" /> Sending…</> : <><PaperAirplaneIcon className="mr-2 h-5 w-5" /> Send email</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
